/**
 * Accounting preview — DEMO FIXTURES ONLY.
 *
 * Every figure below is invented. It exists so the /accounting pages can be
 * walked through before the accounting module is built. Nothing here touches
 * the API or the database.
 *
 * The numbers are deliberately consistent with each other, because the people
 * reading these screens can add:
 *   - the trial balance foots (total debits === total credits)
 *   - the balance sheet foots (assets === liabilities + equity)
 *   - cumulative earnings on the balance sheet === income-statement net income
 *   - the AP aging buckets sum to the open supplier-invoice list, which in
 *     turn equals the 2000 Accounts Payable balance
 *   - inventory valuation === the 1100 Merchandise Inventory balance
 *   - the billing statement's VAT decomposition is exact at 12% VAT-inclusive
 *
 * `sample-data.test.ts` asserts all of the above, so a careless edit here
 * fails CI instead of failing in front of a client.
 */

export const PERIOD_LABEL = 'July 2026';
export const AS_OF_LABEL = '31 July 2026';
export const POSTING_START_LABEL = '1 July 2026';

/** ₱ with thousands separators and 2dp. */
export function peso(n: number): string {
  return `₱${n.toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** ₱ with no centavos — for headline tiles. */
export function pesoShort(n: number): string {
  return `₱${n.toLocaleString('en-PH', { maximumFractionDigits: 0 })}`;
}

// ---------------------------------------------------------------------------
// Chart of accounts
// ---------------------------------------------------------------------------

export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

export type Account = {
  code: string;
  name: string;
  type: AccountType;
  /** Balance as of AS_OF_LABEL. Natural sign for the account type. */
  balance: number;
  active: boolean;
  /** Shown on the CoA page as a plain-language hint. */
  note?: string;
};

export const ACCOUNTS: Account[] = [
  // Assets
  {
    code: '1000',
    name: 'Cash on Hand — POS',
    type: 'asset',
    balance: 185_400,
    active: true,
    note: 'Counter collections per branch',
  },
  { code: '1010', name: 'Cash in Bank', type: 'asset', balance: 3_142_600, active: true },
  {
    code: '1020',
    name: 'Payment Gateway Clearing',
    type: 'asset',
    balance: 268_900,
    active: true,
    note: 'Captured online, not yet settled',
  },
  { code: '1030', name: 'Accounts Receivable — COD', type: 'asset', balance: 96_750, active: true },
  { code: '1100', name: 'Merchandise Inventory', type: 'asset', balance: 2_884_300, active: true },
  { code: '1200', name: 'Input VAT', type: 'asset', balance: 214_700, active: true },
  // Liabilities
  {
    code: '2000',
    name: 'Accounts Payable — Trade',
    type: 'liability',
    balance: 1_246_780,
    active: true,
  },
  {
    code: '2010',
    name: 'Goods Received Not Invoiced',
    type: 'liability',
    balance: 184_500,
    active: true,
    note: 'Received, supplier invoice pending',
  },
  {
    code: '2100',
    name: 'Member Wallet Liability',
    type: 'liability',
    balance: 1_392_450,
    active: true,
  },
  {
    code: '2110',
    name: 'Merchant Wallet Liability',
    type: 'liability',
    balance: 218_600,
    active: true,
  },
  {
    code: '2120',
    name: 'Gift Certificate Liability',
    type: 'liability',
    balance: 164_300,
    active: true,
  },
  { code: '2130', name: 'Coin Scrip Liability', type: 'liability', balance: 47_900, active: true },
  { code: '2200', name: 'Output VAT Payable', type: 'liability', balance: 486_300, active: true },
  {
    code: '2210',
    name: 'Withholding Tax Payable',
    type: 'liability',
    balance: 68_400,
    active: true,
  },
  {
    code: '2300',
    name: 'Customer Deposits (Unearned)',
    type: 'liability',
    balance: 112_750,
    active: true,
    note: 'Paid, not yet delivered',
  },
  {
    code: '2400',
    name: 'Payouts Payable — Members',
    type: 'liability',
    balance: 305_600,
    active: true,
  },
  {
    code: '2410',
    name: 'Payouts Payable — Merchants',
    type: 'liability',
    balance: 87_400,
    active: true,
  },
  {
    code: '2900',
    name: 'Other Current Liabilities',
    type: 'liability',
    balance: 24_720,
    active: true,
  },
  // Equity
  { code: '3000', name: "Owner's Capital", type: 'equity', balance: 1_000_000, active: true },
  {
    code: '3100',
    name: 'Retained Earnings',
    type: 'equity',
    balance: 0,
    active: true,
    note: 'Rolls forward at year-end close',
  },
  {
    code: '3900',
    name: 'Opening Balance Equity',
    type: 'equity',
    balance: 145_900,
    active: true,
    note: 'Offsets the go-live opening entry',
  },
  // Income
  {
    code: '4000',
    name: 'Sales Revenue — Branch / POS',
    type: 'income',
    balance: 2_450_000,
    active: true,
  },
  {
    code: '4010',
    name: 'Sales Revenue — Online',
    type: 'income',
    balance: 1_280_000,
    active: true,
  },
  {
    code: '4020',
    name: 'Sales Revenue — Kits / Activation',
    type: 'income',
    balance: 890_000,
    active: true,
  },
  {
    code: '4100',
    name: 'Sales Returns & Allowances',
    type: 'income',
    balance: -62_000,
    active: true,
    note: 'Contra-revenue',
  },
  { code: '4200', name: 'Shipping Income', type: 'income', balance: 48_500, active: true },
  { code: '4300', name: 'Platform Fee Income', type: 'income', balance: 86_400, active: true },
  { code: '4310', name: 'Payout Fee Income', type: 'income', balance: 24_300, active: true },
  { code: '4900', name: 'Other Income', type: 'income', balance: 17_500, active: true },
  // Expenses
  { code: '5000', name: 'Cost of Goods Sold', type: 'expense', balance: 2_032_800, active: true },
  {
    code: '5100',
    name: 'Inventory Shrinkage & Write-offs',
    type: 'expense',
    balance: 18_250,
    active: true,
  },
  {
    code: '6000',
    name: 'Commission Expense — Cash',
    type: 'expense',
    balance: 1_155_000,
    active: true,
  },
  {
    code: '6010',
    name: 'Commission Expense — Scrip',
    type: 'expense',
    balance: 138_600,
    active: true,
  },
  { code: '6020', name: 'Top-up Bonus Expense', type: 'expense', balance: 42_000, active: true },
  { code: '6100', name: 'Gateway & Bank Fees', type: 'expense', balance: 31_400, active: true },
  { code: '6900', name: 'Misc / Adjustments', type: 'expense', balance: 9_600, active: true },
];

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  income: 'Income',
  expense: 'Expense',
};

// ---------------------------------------------------------------------------
// Trial balance (derived from ACCOUNTS so it can never drift)
// ---------------------------------------------------------------------------

export type TrialBalanceRow = { code: string; name: string; debit: number; credit: number };

/** Debit-natured: assets, expenses, and contra-revenue (negative income). */
function isDebitSide(a: Account): boolean {
  if (a.type === 'asset' || a.type === 'expense') return true;
  return a.type === 'income' && a.balance < 0;
}

export const TRIAL_BALANCE: TrialBalanceRow[] = ACCOUNTS.filter((a) => a.balance !== 0).map((a) => {
  const amount = Math.abs(a.balance);
  return isDebitSide(a)
    ? { code: a.code, name: a.name, debit: amount, credit: 0 }
    : { code: a.code, name: a.name, debit: 0, credit: amount };
});

export const TRIAL_BALANCE_TOTALS = TRIAL_BALANCE.reduce(
  (acc, r) => ({ debit: acc.debit + r.debit, credit: acc.credit + r.credit }),
  { debit: 0, credit: 0 },
);

// ---------------------------------------------------------------------------
// Income statement (derived)
// ---------------------------------------------------------------------------

function bal(code: string): number {
  return ACCOUNTS.find((a) => a.code === code)?.balance ?? 0;
}

const GROSS_SALES = bal('4000') + bal('4010') + bal('4020');
const RETURNS = Math.abs(bal('4100'));
export const NET_SALES = GROSS_SALES - RETURNS;
const OTHER_INCOME = bal('4200') + bal('4300') + bal('4310') + bal('4900');
export const TOTAL_REVENUE = NET_SALES + OTHER_INCOME;
export const COGS = bal('5000');
export const GROSS_PROFIT = TOTAL_REVENUE - COGS;
/** Margin on goods only — the figure an operator means by "gross margin". */
export const GOODS_MARGIN = NET_SALES - COGS;
export const GOODS_MARGIN_PCT = (GOODS_MARGIN / NET_SALES) * 100;
export const OPERATING_EXPENSES =
  bal('5100') + bal('6000') + bal('6010') + bal('6020') + bal('6100') + bal('6900');
export const NET_INCOME = GROSS_PROFIT - OPERATING_EXPENSES;

export const INCOME_STATEMENT = {
  revenue: [
    { label: 'Sales — Branch / POS', amount: bal('4000') },
    { label: 'Sales — Online', amount: bal('4010') },
    { label: 'Sales — Kits / Activation', amount: bal('4020') },
    { label: 'Less: Sales returns & allowances', amount: -RETURNS },
  ],
  netSales: NET_SALES,
  otherIncome: [
    { label: 'Shipping income', amount: bal('4200') },
    { label: 'Platform fee income', amount: bal('4300') },
    { label: 'Payout fee income', amount: bal('4310') },
    { label: 'Other income', amount: bal('4900') },
  ],
  totalRevenue: TOTAL_REVENUE,
  cogs: COGS,
  grossProfit: GROSS_PROFIT,
  expenses: [
    { label: 'Commission expense — cash', amount: bal('6000') },
    { label: 'Commission expense — scrip', amount: bal('6010') },
    { label: 'Top-up bonus expense', amount: bal('6020') },
    { label: 'Gateway & bank fees', amount: bal('6100') },
    { label: 'Inventory shrinkage & write-offs', amount: bal('5100') },
    { label: 'Misc / adjustments', amount: bal('6900') },
  ],
  totalExpenses: OPERATING_EXPENSES,
  netIncome: NET_INCOME,
};

// ---------------------------------------------------------------------------
// Balance sheet (derived)
// ---------------------------------------------------------------------------

const assetRows = ACCOUNTS.filter((a) => a.type === 'asset');
const liabilityRows = ACCOUNTS.filter((a) => a.type === 'liability');
const equityRows = ACCOUNTS.filter((a) => a.type === 'equity');

export const BALANCE_SHEET = {
  assets: assetRows.map((a) => ({ label: `${a.code} ${a.name}`, amount: a.balance })),
  totalAssets: assetRows.reduce((s, a) => s + a.balance, 0),
  liabilities: liabilityRows.map((a) => ({ label: `${a.code} ${a.name}`, amount: a.balance })),
  totalLiabilities: liabilityRows.reduce((s, a) => s + a.balance, 0),
  equity: [
    ...equityRows.map((a) => ({ label: `${a.code} ${a.name}`, amount: a.balance })),
    // No period close yet, so earnings since go-live are shown as a derived
    // line rather than sitting in 3100. This is what makes the sheet foot.
    { label: `Cumulative earnings since ${POSTING_START_LABEL}`, amount: NET_INCOME },
  ],
  totalEquity: equityRows.reduce((s, a) => s + a.balance, 0) + NET_INCOME,
};

// ---------------------------------------------------------------------------
// General journal
// ---------------------------------------------------------------------------

export type JournalLine = { code: string; name: string; debit: number; credit: number };

export type JournalEntry = {
  entryNo: string;
  date: string;
  memo: string;
  /** Where the entry came from — system-posted unless "Manual". */
  source: string;
  sourceRef: string;
  amount: number;
  lines: JournalLine[];
};

export const JOURNAL: JournalEntry[] = [
  {
    entryNo: 'JE-00419',
    date: '2026-07-29',
    memo: 'Order SO-01288 completed — revenue recognised',
    source: 'Order completed',
    sourceRef: 'SO-01288',
    amount: 16_420,
    lines: [
      { code: '2300', name: 'Customer Deposits (Unearned)', debit: 16_420, credit: 0 },
      { code: '4000', name: 'Sales Revenue — Branch / POS', debit: 0, credit: 14_500 },
      { code: '2200', name: 'Output VAT Payable', debit: 0, credit: 1_740 },
      { code: '4200', name: 'Shipping Income', debit: 0, credit: 180 },
    ],
  },
  {
    entryNo: 'JE-00418',
    date: '2026-07-29',
    memo: 'Counter payment received — Manila Main',
    source: 'POS payment',
    sourceRef: 'POS-00892',
    amount: 16_420,
    lines: [
      { code: '1000', name: 'Cash on Hand — POS', debit: 16_420, credit: 0 },
      { code: '2300', name: 'Customer Deposits (Unearned)', debit: 0, credit: 16_420 },
    ],
  },
  {
    entryNo: 'JE-00420',
    date: '2026-07-29',
    memo: 'Cost of goods sold — SO-01288',
    source: 'Order completed',
    sourceRef: 'SO-01288',
    amount: 7_105,
    lines: [
      { code: '5000', name: 'Cost of Goods Sold', debit: 7_105, credit: 0 },
      { code: '1100', name: 'Merchandise Inventory', debit: 0, credit: 7_105 },
    ],
  },
  {
    entryNo: 'JE-00421',
    date: '2026-07-29',
    memo: 'Commissions earned on SO-01288',
    source: 'Commission',
    sourceRef: 'evt-88213',
    amount: 3_625,
    lines: [
      { code: '6000', name: 'Commission Expense — Cash', debit: 3_625, credit: 0 },
      { code: '2100', name: 'Member Wallet Liability', debit: 0, credit: 3_625 },
    ],
  },
  {
    entryNo: 'JE-00423',
    date: '2026-07-28',
    memo: 'Supplier invoice SI-2026-0412 — Batangas Herbal Supply',
    source: 'Supplier invoice',
    sourceRef: 'SI-2026-0412',
    amount: 284_480,
    lines: [
      { code: '2010', name: 'Goods Received Not Invoiced', debit: 254_000, credit: 0 },
      { code: '1200', name: 'Input VAT', debit: 30_480, credit: 0 },
      { code: '2000', name: 'Accounts Payable — Trade', debit: 0, credit: 284_480 },
    ],
  },
  {
    entryNo: 'JE-00422',
    date: '2026-07-28',
    memo: 'Goods received against PO-10248',
    source: 'Receiving',
    sourceRef: 'RCV-MNL01-2026-0187',
    amount: 254_000,
    lines: [
      { code: '1100', name: 'Merchandise Inventory', debit: 254_000, credit: 0 },
      { code: '2010', name: 'Goods Received Not Invoiced', debit: 0, credit: 254_000 },
    ],
  },
  {
    entryNo: 'JE-00424',
    date: '2026-07-27',
    memo: 'Payout batch PB-2026-07-04 released',
    source: 'Payout',
    sourceRef: 'PB-2026-07-04',
    amount: 486_000,
    lines: [
      { code: '2400', name: 'Payouts Payable — Members', debit: 486_000, credit: 0 },
      { code: '1010', name: 'Cash in Bank', debit: 0, credit: 437_400 },
      { code: '2210', name: 'Withholding Tax Payable', debit: 0, credit: 24_300 },
      { code: '4310', name: 'Payout Fee Income', debit: 0, credit: 24_300 },
    ],
  },
  {
    entryNo: 'JE-00426',
    date: '2026-07-26',
    memo: 'Online payments captured — settlement pending',
    source: 'Gateway payment',
    sourceRef: 'DP-8823401',
    amount: 48_600,
    lines: [
      { code: '1020', name: 'Payment Gateway Clearing', debit: 48_600, credit: 0 },
      { code: '2300', name: 'Customer Deposits (Unearned)', debit: 0, credit: 48_600 },
    ],
  },
  {
    entryNo: 'JE-00425',
    date: '2026-07-26',
    memo: 'Write-off — expired stock, Cebu branch',
    source: 'Write-off',
    sourceRef: 'WO-CEB01-2026-0032',
    amount: 18_250,
    lines: [
      { code: '5100', name: 'Inventory Shrinkage & Write-offs', debit: 18_250, credit: 0 },
      { code: '1100', name: 'Merchandise Inventory', debit: 0, credit: 18_250 },
    ],
  },
  {
    entryNo: 'JE-00401',
    date: '2026-07-01',
    memo: 'Opening balances at go-live',
    source: 'Manual',
    sourceRef: 'opening-2026-07-01',
    amount: 1_145_900,
    lines: [
      { code: '1010', name: 'Cash in Bank', debit: 1_000_000, credit: 0 },
      { code: '1100', name: 'Merchandise Inventory', debit: 145_900, credit: 0 },
      { code: '3000', name: "Owner's Capital", debit: 0, credit: 1_000_000 },
      { code: '3900', name: 'Opening Balance Equity', debit: 0, credit: 145_900 },
    ],
  },
];

// ---------------------------------------------------------------------------
// Supplier invoices / AP
// ---------------------------------------------------------------------------

export type AgingBucket = 'Current' | '1–30 days' | '31–60 days' | '61–90 days' | 'Over 90 days';

export type SupplierInvoice = {
  invoiceNo: string;
  supplier: string;
  invoiceDate: string;
  dueDate: string;
  amount: number;
  bucket: AgingBucket;
  match: 'Matched' | 'Variance';
  variance?: number;
  poNo: string;
};

export const SUPPLIER_INVOICES: SupplierInvoice[] = [
  {
    invoiceNo: 'SI-2026-0412',
    supplier: 'Batangas Herbal Supply',
    invoiceDate: '2026-07-24',
    dueDate: '2026-08-23',
    amount: 284_480,
    bucket: 'Current',
    match: 'Matched',
    poNo: 'PO-10248',
  },
  {
    invoiceNo: 'SI-2026-0408',
    supplier: 'Pure Leaf Manufacturing',
    invoiceDate: '2026-07-18',
    dueDate: '2026-08-17',
    amount: 196_300,
    bucket: 'Current',
    match: 'Matched',
    poNo: 'PO-10239',
  },
  {
    invoiceNo: 'SI-2026-0401',
    supplier: 'Nutriwell Packaging Inc.',
    invoiceDate: '2026-07-10',
    dueDate: '2026-08-09',
    amount: 142_800,
    bucket: 'Current',
    match: 'Variance',
    variance: 3_400,
    poNo: 'PO-10231',
  },
  {
    invoiceNo: 'SI-2026-0388',
    supplier: 'Cebu Logistics Supply',
    invoiceDate: '2026-06-28',
    dueDate: '2026-07-28',
    amount: 168_400,
    bucket: '1–30 days',
    match: 'Matched',
    poNo: 'PO-10218',
  },
  {
    invoiceNo: 'SI-2026-0379',
    supplier: 'Metro Bottling Corp.',
    invoiceDate: '2026-06-20',
    dueDate: '2026-07-20',
    amount: 124_900,
    bucket: '1–30 days',
    match: 'Matched',
    poNo: 'PO-10207',
  },
  {
    invoiceNo: 'SI-2026-0361',
    supplier: 'Davao Packaging Solutions',
    invoiceDate: '2026-05-26',
    dueDate: '2026-06-25',
    amount: 187_500,
    bucket: '31–60 days',
    match: 'Variance',
    variance: 12_250,
    poNo: 'PO-10184',
  },
  {
    invoiceNo: 'SI-2026-0344',
    supplier: 'Luzon Label Printing',
    invoiceDate: '2026-04-22',
    dueDate: '2026-05-22',
    amount: 98_600,
    bucket: '61–90 days',
    match: 'Matched',
    poNo: 'PO-10162',
  },
  {
    invoiceNo: 'SI-2026-0298',
    supplier: 'Northern Glass Works',
    invoiceDate: '2026-01-30',
    dueDate: '2026-03-01',
    amount: 43_800,
    bucket: 'Over 90 days',
    match: 'Variance',
    variance: 1_900,
    poNo: 'PO-10098',
  },
];

export const AGING_BUCKETS: AgingBucket[] = [
  'Current',
  '1–30 days',
  '31–60 days',
  '61–90 days',
  'Over 90 days',
];

/**
 * Ordinal ramp for the aging buckets: ONE hue, monotone light→dark, so the
 * colour itself carries "further overdue". Validated (monotone lightness,
 * adjacent ΔL ≥ 0.06, light end ≥ 2:1 on the card surface, single hue) —
 * do not hand-tweak a step without re-validating the whole ramp.
 */
export const AGING_RAMP: Record<AgingBucket, string> = {
  Current: '#e2a53d',
  '1–30 days': '#c68a22',
  '31–60 days': '#a06d0d',
  '61–90 days': '#7a530a',
  'Over 90 days': '#513607',
};

export const AGING_TOTALS: { bucket: AgingBucket; amount: number }[] = AGING_BUCKETS.map((b) => ({
  bucket: b,
  amount: SUPPLIER_INVOICES.filter((i) => i.bucket === b).reduce((s, i) => s + i.amount, 0),
}));

export const AP_TOTAL = SUPPLIER_INVOICES.reduce((s, i) => s + i.amount, 0);
export const AP_OVERDUE = AGING_TOTALS.filter((b) => b.bucket !== 'Current').reduce(
  (s, b) => s + b.amount,
  0,
);

// ---------------------------------------------------------------------------
// Billing statement (12% VAT-inclusive)
// ---------------------------------------------------------------------------

export const VAT_PCT = 12;

export type BillingLine = { sku: string; name: string; qty: number; unitPrice: number };

export const BILLING = {
  docNo: 'BILL-MNL01-00347',
  orderCode: 'SO-01288',
  issuedAt: '29 July 2026, 2:14 PM',
  buyer: {
    name: 'Maria Clara Santos',
    address: '18 Mabini Street, Barangay San Roque, Quezon City, Metro Manila 1109',
    phone: '0917 555 0184',
    tin: '284-771-093-000',
  },
  branch: {
    name: 'TESLABARC Manila Main',
    code: 'MNL01',
    address: '507-508 Medalla Bldg., EDSA cor. McArthur Ave., Cubao, Quezon City',
  },
  lines: [
    { sku: 'TB-COF-20', name: 'Bio-Active Coffee 20s', qty: 4, unitPrice: 1_680 },
    { sku: 'TB-GLU-60', name: 'Glutathione Complex 60 caps', qty: 2, unitPrice: 2_240 },
    { sku: 'TB-TEA-15', name: 'Herbal Detox Tea 15s', qty: 6, unitPrice: 840 },
  ] as BillingLine[],
  shippingFee: 180,
  paymentMethod: 'Cash — counter',
};

export const BILLING_LINE_TOTALS = BILLING.lines.map((l) => {
  const lineTotal = l.qty * l.unitPrice;
  // VAT-inclusive decomposition: VAT is the remainder so the legs always sum
  // back to the line total exactly.
  const vatable = Math.round((lineTotal / (1 + VAT_PCT / 100)) * 100) / 100;
  return { ...l, lineTotal, vatable, vat: Math.round((lineTotal - vatable) * 100) / 100 };
});

export const BILLING_TOTALS = (() => {
  const goods = BILLING_LINE_TOTALS.reduce((s, l) => s + l.lineTotal, 0);
  const vatable = BILLING_LINE_TOTALS.reduce((s, l) => s + l.vatable, 0);
  const vat = BILLING_LINE_TOTALS.reduce((s, l) => s + l.vat, 0);
  return { goods, vatable, vat, shipping: BILLING.shippingFee, total: goods + BILLING.shippingFee };
})();

// ---------------------------------------------------------------------------
// Costing & margins
// ---------------------------------------------------------------------------

export type MarginRow = {
  sku: string;
  name: string;
  qty: number;
  revenue: number;
  cost: number;
};

export const MARGIN_ROWS: MarginRow[] = [
  {
    sku: 'TB-COF-20',
    name: 'Bio-Active Coffee 20s',
    qty: 1_240,
    revenue: 2_083_200,
    cost: 918_400,
  },
  {
    sku: 'TB-GLU-60',
    name: 'Glutathione Complex 60 caps',
    qty: 486,
    revenue: 1_088_640,
    cost: 505_440,
  },
  { sku: 'TB-TEA-15', name: 'Herbal Detox Tea 15s', qty: 892, revenue: 749_280, cost: 330_040 },
  { sku: 'TB-KIT-STR', name: 'Business Starter Kit', qty: 142, revenue: 852_000, cost: 383_400 },
  {
    sku: 'TB-COL-10',
    name: 'Collagen Peptide Drink 10s',
    qty: 374,
    revenue: 448_800,
    cost: 213_180,
  },
  { sku: 'TB-MOR-90', name: 'Moringa Capsules 90s', qty: 268, revenue: 281_400, cost: 128_640 },
  { sku: 'TB-VTC-60', name: 'Vitamin C Complex 60s', qty: 210, revenue: 176_400, cost: 84_000 },
  { sku: 'TB-SLM-12', name: 'Slimming Coffee 12s', qty: 156, revenue: 140_400, cost: 68_640 },
];

export const VALUATION = {
  atCost: bal('1100'),
  skus: 142,
  units: 38_470,
  uncostedLines: 0,
};

export type LowStockRow = {
  sku: string;
  name: string;
  branch: string;
  onHand: number;
  reorderPoint: number;
};

export const LOW_STOCK: LowStockRow[] = [
  {
    sku: 'TB-GLU-60',
    name: 'Glutathione Complex 60 caps',
    branch: 'Cebu',
    onHand: 18,
    reorderPoint: 60,
  },
  {
    sku: 'TB-TEA-15',
    name: 'Herbal Detox Tea 15s',
    branch: 'Manila Main',
    onHand: 42,
    reorderPoint: 120,
  },
  { sku: 'TB-VTC-60', name: 'Vitamin C Complex 60s', branch: 'Davao', onHand: 7, reorderPoint: 40 },
  {
    sku: 'TB-COL-10',
    name: 'Collagen Peptide Drink 10s',
    branch: 'Cebu',
    onHand: 25,
    reorderPoint: 50,
  },
];

// ---------------------------------------------------------------------------
// Monthly net-sales series (the dashboard trend line)
// ---------------------------------------------------------------------------

export type SalesPoint = { month: string; netSales: number };

/** Six months ending with the current period; the last point IS `NET_SALES`. */
export const MONTHLY_SALES: SalesPoint[] = [
  { month: 'Feb', netSales: 3_180_000 },
  { month: 'Mar', netSales: 3_420_000 },
  { month: 'Apr', netSales: 3_905_000 },
  { month: 'May', netSales: 3_742_000 },
  { month: 'Jun', netSales: 4_055_160 },
  { month: 'Jul', netSales: NET_SALES },
];

const priorMonthSales = MONTHLY_SALES[MONTHLY_SALES.length - 2]!.netSales;
/** Derived, never hardcoded — so the tile can't disagree with the chart. */
export const NET_SALES_DELTA_PCT = ((NET_SALES - priorMonthSales) / priorMonthSales) * 100;

// ---------------------------------------------------------------------------
// Operating-expense breakdown (largest first)
// ---------------------------------------------------------------------------

export const EXPENSE_BREAKDOWN = [...INCOME_STATEMENT.expenses]
  .map((e) => ({ label: e.label.replace(/ — /g, ' — '), amount: e.amount }))
  .sort((a, b) => b.amount - a.amount);

// ---------------------------------------------------------------------------
// Cash & money-in-transit accounts (the "bank accounts" card)
// ---------------------------------------------------------------------------

export const CASH_ACCOUNTS = ['1000', '1010', '1020', '1030'].map((code) => {
  const a = ACCOUNTS.find((x) => x.code === code)!;
  return { code: a.code, name: a.name, balance: a.balance, note: a.note };
});

export const CASH_TOTAL = CASH_ACCOUNTS.reduce((s, a) => s + a.balance, 0);

// ---------------------------------------------------------------------------
// Overview tiles
// ---------------------------------------------------------------------------

export const OVERVIEW = {
  netSales: NET_SALES,
  netSalesDeltaPct: NET_SALES_DELTA_PCT,
  goodsMargin: GOODS_MARGIN,
  goodsMarginPct: GOODS_MARGIN_PCT,
  cashAndBank: bal('1000') + bal('1010'),
  gatewayInTransit: bal('1020'),
  apOverdue: AP_OVERDUE,
  apCurrent: AP_TOTAL - AP_OVERDUE,
  apTotal: AP_TOTAL,
  netIncome: NET_INCOME,
  totalRevenue: TOTAL_REVENUE,
  operatingExpenses: OPERATING_EXPENSES,
  cogs: COGS,
  entriesPosted: 1_284,
  unpostedDocuments: 0,
  lastPostedAt: '29 July 2026, 2:41 PM',
};
